import makeStyles from '@mui/styles/makeStyles';
import { Theme } from '@mui/material/styles';
import { Draggable } from 'react-beautiful-dnd';
import { Add, RestartAlt, Save, Clear, Edit, ExpandMore, ExpandLess, DragIndicator } from '@mui/icons-material';
import { Button, Typography, Divider, FormControlLabel, Switch, Box, Collapse, IconButton, ListItemText, ListItem, TextField } from '@mui/material';
import { ICustomSubNavigationDetail } from '../../../models';
import { Link } from '../../../components/link';
import { ExternalLink } from '../../../components/link/ExternalLink';
import { SortableNavSubContainer } from './SortableNavSubContainer';
import { getSecuritySensitiveRandomNumber } from '../../../helpers';

interface ISortableItem {
  isNavExpanded: {
    [item: string]: boolean;
  };
  isEditing: {
    [id: string]: boolean;
  };
  setEditing: (val: { [id: number]: boolean }) => void;
  setNavExpanded: (val: { [item: string]: boolean }) => void;
  item: ICustomSubNavigationDetail;
  index: number;
  setNavItems: (items: ICustomSubNavigationDetail[]) => void;
  navItems: ICustomSubNavigationDetail[];
  originalNavItems: ICustomSubNavigationDetail[];
}

export const SortableNavGroup = ({ originalNavItems, setEditing, isEditing, navItems, item, setNavExpanded, isNavExpanded, index, setNavItems }: ISortableItem) => {
  const isInEditMode = isEditing[item.customNavigationId];
  const classes = useStyles();
  return (
    <Draggable key={item.customNavigationId} draggableId={`${item.customNavigationId}`} index={index} isDragDisabled={isInEditMode}>
      {(provided, snapshot) => (
        <div>
          <div ref={provided.innerRef} {...provided.draggableProps}>
            {isInEditMode && <Divider sx={{ marginTop: '0.5rem', marginBottom: '0.5rem' }} />}
            <ListItem sx={{ padding: `4px 0` }}>
              {isInEditMode ? (
                <>
                  <Box
                    sx={{
                      margin: '0.5rem 0',
                      display: 'flex',
                      flexDirection: 'column',
                      flex: 1,
                      marginRight: '1rem'
                    }}
                  >
                    <TextField
                      size="small"
                      label="Name"
                      autoComplete="nope"
                      variant="outlined"
                      value={item.name}
                      required
                      fullWidth
                      sx={{ marginBottom: '0.75rem' }}
                      onChange={e => {
                        const copy = JSON.parse(JSON.stringify(navItems));
                        copy[index].name = e.target.value;
                        setNavItems(copy);
                      }}
                      error={isInEditMode && !navItems?.[index]?.name ? true : false}
                      helperText={isInEditMode && !navItems?.[index]?.name && 'Required'}
                    />
                    <TextField
                      size="small"
                      label="URL"
                      autoComplete="nope"
                      variant="outlined"
                      value={item.url}
                      required={item.openNewTab}
                      fullWidth
                      sx={{ marginBottom: '0.75rem' }}
                      onChange={e => {
                        const copy = JSON.parse(JSON.stringify(navItems));
                        copy[index].url = e.target.value;
                        setNavItems(copy);
                      }}
                      error={
                        // not a valid relative route
                        (!item?.openNewTab && item?.url?.startsWith('http')) ||
                        // they selected external link but the link doesn't start with http
                        (item?.openNewTab && !item?.url?.startsWith('http')) ||
                        // link contains white space
                        item?.url?.indexOf(' ') >= 0
                          ? true
                          : false
                      }
                      helperText={
                        !item?.openNewTab && item?.url?.startsWith('http')
                          ? "Relative links can't start with 'http'"
                          : item?.openNewTab && !item?.url?.startsWith('http')
                          ? 'Must start with http'
                          : item?.url?.indexOf(' ') >= 0
                          ? 'Must not contain white space'
                          : ''
                      }
                    />
                    <FormControlLabel
                      control={
                        <Switch
                          color="primary"
                          checked={item.openNewTab}
                          id="external"
                          onChange={e => {
                            const copy = JSON.parse(JSON.stringify(navItems));
                            copy[index].openNewTab = Boolean(e.target.checked);
                            setNavItems(copy);
                          }}
                        />
                      }
                      label="External Link?"
                    />
                  </Box>
                </>
              ) : (
                <>
                  <ListItemText className={classes.listItemText}>
                    <div>
                      <Typography>{item.name}</Typography>
                      {item.openNewTab && item.url && (
                        <ExternalLink to={item.url} target="_blank" className={classes.externalLink}>
                          {item.url}
                        </ExternalLink>
                      )}
                      {item.url && !item.openNewTab && <Link to={`/${item.url}`}>{item.url}</Link>}
                    </div>
                    {item.subNavigation && item.subNavigation.length > 0 && !isInEditMode && (
                      <IconButton
                        sx={{ padding: '2px' }}
                        onClick={() => {
                          setNavExpanded({
                            ...isNavExpanded,
                            [item.name]: !isNavExpanded[item.name]
                          });
                        }}
                        aria-expanded={isNavExpanded[item.name]}
                        aria-label="show more nav items"
                      >
                        {isNavExpanded[item.name] ? <ExpandLess /> : <ExpandMore />}
                      </IconButton>
                    )}
                  </ListItemText>
                </>
              )}

              <Box display="flex" flexDirection={isInEditMode ? 'column' : 'row'} alignSelf={isInEditMode ? 'flex-start' : 'auto'}>
                {!isInEditMode && (
                  <div {...provided.dragHandleProps}>
                    <IconButton sx={{ cursor: 'grab', p: '2px' }}>
                      <DragIndicator />
                    </IconButton>
                  </div>
                )}
                <IconButton
                  onClick={e => {
                    setEditing({
                      [item.customNavigationId]: !isInEditMode
                    });
                  }}
                  sx={{ p: '2px' }}
                  title={isInEditMode ? 'Save' : 'Edit'}
                  disabled={isInEditMode && (!item.name || (item.openNewTab && !item.url))}
                  color={isInEditMode ? 'primary' : 'inherit'}
                >
                  {isInEditMode ? <Save /> : <Edit />}
                </IconButton>
                {isInEditMode && (
                  <IconButton
                    title="Reset"
                    sx={{ p: '2px' }}
                    color="secondary"
                    onClick={e => {
                      setNavItems(originalNavItems);
                    }}
                  >
                    <RestartAlt />
                  </IconButton>
                )}
                <IconButton
                  color="error"
                  title="Remove"
                  sx={{ p: '2px' }}
                  onClick={() => {
                    const result = window.confirm('Are you sure you want to remove this nav item?');
                    if (result) {
                      setNavItems(navItems.filter(i => i.customNavigationId !== item.customNavigationId));
                    }
                  }}
                >
                  <Clear />
                </IconButton>
              </Box>
            </ListItem>
            {isInEditMode && <Divider />}
            <Collapse in={isNavExpanded[item.name]} timeout="auto" unmountOnExit>
              <SortableNavSubContainer
                originalNavItems={originalNavItems}
                isEditing={isEditing}
                setEditing={setEditing}
                item={item}
                subLinks={item.subNavigation}
                navItems={navItems}
                setNavItems={setNavItems}
                setNavExpanded={setNavExpanded}
                isNavExpanded={isNavExpanded}
              />
            </Collapse>
            {/* button only used for parent nav items that don't have any child links */}
            {item?.subNavigation?.length === 0 && (
              <Button
                color="secondary"
                startIcon={<Add />}
                sx={{ fontSize: '12px', padding: '0', marginLeft: '1rem' }}
                onClick={() => {
                  const copy = JSON.parse(JSON.stringify(navItems));
                  const parentIndex = navItems.findIndex(i => i.customNavigationId === item.customNavigationId);
                  const randomNumber = Math.floor(getSecuritySensitiveRandomNumber() * (100000 - 1 + 1) + 1);
                  copy[parentIndex].subNavigation.push({
                    customNavigationId: randomNumber,
                    name: 'New Sub Nav',
                    url: 'url',
                    isNew: true,
                    sortOrder: copy[parentIndex].subNavigation.length + 1
                  });
                  setEditing({
                    [randomNumber]: true
                  });
                  setNavExpanded({
                    ...isNavExpanded,
                    [item.name]: !isNavExpanded[item.name]
                  });
                  setNavItems(copy);
                }}
              >
                Add Child Nav Item
              </Button>
            )}
          </div>
          {provided.placeholder}
        </div>
      )}
    </Draggable>
  );
};

const useStyles = makeStyles<Theme>(theme => ({
  listItemText: {
    '& span': {
      display: 'flex',
      alignItems: 'center'
    }
  },
  externalLink: {
    wordBreak: 'break-word'
  }
}));
